import React, { useEffect, useRef, useState } from "react";

import { Field, Form, Formik } from "formik";
import { format, isValid, parse, isAfter } from "date-fns";
import * as Yup from "yup";

import {
  Button,
  Flex,
  Input,
  InputGroup,
  Grid,
  GridItem,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";

import CustomSelect from "../CustomSelect";
import { CustomDatepicker } from "../CustomDatepicker";
import AuditoriaService from "../../../main/services/AuditoriaService";
import { Permission, dashboardFunc } from "../../../helpers/Funcionalidades";

const OPCOES_VISAO = [
  { value: "SERVICO", label: "Serviço de Acolhimento" },
  { value: "CRIANCA", label: "Criança e Adolescente" },
];

const initialFilters = {
  visao: OPCOES_VISAO[0],
  nomeServico: "",
  nomeCrianca: "",
  servico: "",
  dataInicial: "",
  dataFinal: "",
  login: "",
  perfil: "",
  grupoSubgrupoCampo: "",
  acao: "",
};

const schema = Yup.object().shape({
  dataInicial: Yup.string()
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!value) return true;
      return (
        value.length === 10 && isValid(parse(value, "dd/MM/yyyy", new Date()))
      );
    })
    .test(
      "notFuture",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) return true;
        return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
      },
    ),
  dataFinal: Yup.string()
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!value) return true;
      return (
        value.length === 10 && isValid(parse(value, "dd/MM/yyyy", new Date()))
      );
    })
    .test(
      "notFuture",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) return true;
        return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
      },
    ),
});

const STORAGE_KEY = "auditoria-filter";

export default function FiltersAuditoria({
  onSearch,
  onClear,
  onVisaoChange,
  roleKeycloack,
  loading,
}) {
  const formRef = useRef(null);
  const [initialValues, setInitialValues] = useState(initialFilters);
  // Visão cujo catálogo de grupos/campos está carregado — muda junto com o
  // tipo de pesquisa e recarrega o catálogo (gruposCampos é filtrado por visão).
  const [visaoCatalogo, setVisaoCatalogo] = useState(null);
  const ultimaRequisicaoCatalogo = useRef(0);
  const [logins, setLogins] = useState({ options: [], isLoading: true });
  const [perfis, setPerfis] = useState({ options: [], isLoading: true });
  const [grupos, setGrupos] = useState({ options: [], isLoading: true });
  const [acoes, setAcoes] = useState([]);
  // `disponivel` só vira true quando o backend devolve `servicos` no catálogo;
  // até lá o combo de Serviço (visão Criança) fica escondido.
  const [servicos, setServicos] = useState({
    options: [],
    isLoading: true,
    disponivel: false,
  });

  useEffect(() => {
    const blob = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    if (blob.values) setInitialValues(blob.values);
    setVisaoCatalogo(blob.values?.visao?.value || OPCOES_VISAO[0].value);
  }, []);

  useEffect(() => {
    if (!visaoCatalogo) return;

    // Descarta a resposta de uma visão anterior que chegue atrasada.
    const requisicao = ++ultimaRequisicaoCatalogo.current;
    setGrupos({ options: [], isLoading: true });

    AuditoriaService.buscarCatalogoFiltros(visaoCatalogo).then((res) => {
      if (requisicao !== ultimaRequisicaoCatalogo.current) return;

      const {
        usuarios,
        perfis: perfisCatalogo,
        gruposCampos,
        acoes: acoesCatalogo,
        servicos: servicosCatalogo,
      } = res.data;

      setLogins({ options: usuarios || [], isLoading: false });
      setPerfis({
        options: (perfisCatalogo || []).map((perfil) => ({
          value: perfil,
          label: perfil,
        })),
        isLoading: false,
      });
      const opcoesGrupos = (gruposCampos || []).map((grupo) => ({
        label: grupo.label,
        options: [
          {
            value: grupo.aba,
            label: `Toda a aba ${grupo.label}`,
            isAba: true,
          },
          ...grupo.campos.map((campo) => ({ value: campo, label: campo })),
        ],
      }));
      setGrupos({ options: opcoesGrupos, isLoading: false });

      // Seleção restaurada do sessionStorage que não existe no catálogo da
      // visão atual é descartada.
      const selecionado = formRef.current?.values?.grupoSubgrupoCampo;
      const existe =
        !selecionado ||
        opcoesGrupos.some((grupo) =>
          grupo.options.some(
            (opcao) =>
              opcao.value === selecionado.value &&
              !!opcao.isAba === !!selecionado.isAba,
          ),
        );
      if (!existe) formRef.current.setFieldValue("grupoSubgrupoCampo", "");
      setAcoes(acoesCatalogo || []);
      // Serviços que o usuário logado pode visualizar (filtro da visão Criança).
      setServicos({
        options: (servicosCatalogo || []).map((servico) => ({
          value: servico.id,
          label: servico.nome,
        })),
        isLoading: false,
        disponivel: Array.isArray(servicosCatalogo),
      });
    });
  }, [visaoCatalogo]);

  const handleSubmit = (values) => {
    const blobAtual = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...blobAtual, values }),
    );

    const parseData = (value) =>
      value && isValid(parse(value, "dd/MM/yyyy", new Date()))
        ? format(parse(value, "dd/MM/yyyy", new Date()), "yyyy-MM-dd")
        : "";

    const chips = [];
    if (values.nomeServico)
      chips.push({ label: "Serviço", value: values.nomeServico });
    if (values.nomeCrianca)
      chips.push({ label: "Criança", value: values.nomeCrianca });
    if (values.dataInicial || values.dataFinal) {
      chips.push({
        label: "Período",
        value: `${values.dataInicial || "início"} → ${values.dataFinal || "hoje"}`,
      });
    }
    if (values.login) {
      chips.push({
        label: "Login",
        value: `${values.login.nome} (${values.login.login})`,
      });
    }
    if (values.perfil)
      chips.push({ label: "Perfil", value: values.perfil.label });
    if (values.grupoSubgrupoCampo) {
      chips.push({
        label: values.grupoSubgrupoCampo.isAba ? "Aba" : "Campo",
        value: values.grupoSubgrupoCampo.label,
      });
    }
    if (values.acao) chips.push({ label: "Ação", value: values.acao.label });
    if (values.servico)
      chips.push({ label: "Serviço", value: values.servico.label });

    onSearch(
      {
        nomeServico: values.nomeServico,
        nomeCrianca: values.nomeCrianca,
        idServicoAcolhimento: values.servico ? values.servico.value : "",
        periodoInicial: parseData(values.dataInicial),
        periodoFinal: parseData(values.dataFinal),
        login: values.login ? values.login.login : "",
        perfil: values.perfil ? values.perfil.value : "",
        grupoCampo: values.grupoSubgrupoCampo
          ? values.grupoSubgrupoCampo.value
          : "",
        acao: values.acao ? values.acao.value : "",
      },
      chips,
      values.visao?.value || "SERVICO",
    );
  };

  return (
    <Flex flexDir="column" alignItems="center" w="100%">
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        innerRef={formRef}
      >
        {(props) => (
          <Form style={{ width: "100%" }}>
            <Flex flexDirection="column" alignItems="center">
              <Grid
                templateColumns={{
                  base: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                  xl: "repeat(4, 1fr)",
                }}
                gap="17px"
                w="100%"
                mt="2px"
              >
                <GridItem colSpan={{ base: 1, sm: 2 }}>
                  <CustomSelect
                    placeholder="Selecione o tipo de pesquisa"
                    value={props.values.visao}
                    options={OPCOES_VISAO}
                    onChange={(value) => {
                      // Campo obrigatório — se o usuário limpar (value vem null), volta
                      // pro padrão (Criança) em vez de deixar sem nada selecionado.
                      const novaVisao = value || OPCOES_VISAO[0];
                      if (novaVisao.value === props.values.visao?.value) return;

                      // Troca de tipo limpa todos os filtros da tela e o resultado
                      // da consulta anterior.
                      props.resetForm({
                        values: { ...initialFilters, visao: novaVisao },
                      });
                      setVisaoCatalogo(novaVisao.value);
                      onVisaoChange(novaVisao.value);
                    }}
                  />
                </GridItem>

                {props.values.visao?.value === "SERVICO" ? (
                  <GridItem colSpan={{ base: 1, sm: 2 }}>
                    <Field name="nomeServico">
                      {({ field }) => (
                        <InputGroup>
                          <Input
                            {...field}
                            h="42px"
                            backgroundColor="white"
                            autoComplete="off"
                            placeholder="Digite o Nome do Serviço de Acolhimento"
                          />
                        </InputGroup>
                      )}
                    </Field>
                  </GridItem>
                ) : (
                  <GridItem colSpan={{ base: 1, sm: 2 }}>
                    <Field name="nomeCrianca">
                      {({ field }) => (
                        <InputGroup>
                          <Input
                            {...field}
                            h="42px"
                            backgroundColor="white"
                            autoComplete="off"
                            placeholder="Digite o Nome da Criança e Adolescente"
                          />
                        </InputGroup>
                      )}
                    </Field>
                  </GridItem>
                )}

                {props.values.visao?.value === "CRIANCA" && servicos.disponivel && (
                  <GridItem
                    colStart={{ xl: 3 }}
                    colSpan={{ base: 1, sm: 2 }}
                  >
                    <CustomSelect
                      isClearable
                      placeholder="Selecione o Serviço de Acolhimento"
                      value={props.values.servico || ""}
                      isLoading={servicos.isLoading}
                      options={servicos.options}
                      onChange={(value) => props.setFieldValue("servico", value)}
                    />
                  </GridItem>
                )}

                <GridItem colSpan={1}>
                  <CustomDatepicker
                    value={props.values.dataInicial}
                    setValue={(date) =>
                      props.setFieldValue("dataInicial", date)
                    }
                    placeholder="Data inicial"
                    widthInput="100%"
                  />
                </GridItem>

                <GridItem colSpan={1}>
                  <CustomDatepicker
                    value={props.values.dataFinal}
                    setValue={(date) => props.setFieldValue("dataFinal", date)}
                    placeholder="Data final"
                    widthInput="100%"
                  />
                </GridItem>

                <GridItem colSpan={1}>
                  <CustomSelect
                    isClearable
                    placeholder="Selecione o Login do Usuário"
                    value={props.values.login || ""}
                    isLoading={logins.isLoading}
                    options={logins.options}
                    getOptionLabel={(option) =>
                      `${option.nome} (${option.login})`
                    }
                    getOptionValue={(option) => option.login}
                    onChange={(value) => props.setFieldValue("login", value)}
                  />
                </GridItem>

                <GridItem colSpan={1}>
                  <CustomSelect
                    isClearable
                    placeholder="Selecione o Perfil do Login do Usuário"
                    value={props.values.perfil || ""}
                    isLoading={perfis.isLoading}
                    options={perfis.options}
                    onChange={(value) => props.setFieldValue("perfil", value)}
                  />
                </GridItem>

                <GridItem colSpan={{ base: 1, sm: 2 }}>
                  <CustomSelect
                    isClearable
                    placeholder="Selecione o Grupo, Subgrupo ou campo Modificado"
                    value={props.values.grupoSubgrupoCampo || ""}
                    isLoading={grupos.isLoading}
                    options={grupos.options}
                    onChange={(value) =>
                      props.setFieldValue("grupoSubgrupoCampo", value)
                    }
                  />
                </GridItem>

                <GridItem colSpan={{ base: 1, sm: 2 }}>
                  <CustomSelect
                    isClearable
                    placeholder="Selecione a Ação"
                    value={props.values.acao || ""}
                    options={acoes}
                    onChange={(value) => props.setFieldValue("acao", value)}
                  />
                </GridItem>
              </Grid>

              <SimpleGrid
                mt="20px"
                columns={{ base: 2, lg: 2 }}
                spacing="15px"
                w={{ base: "100%", lg: "32%" }}
              >
                <Permission
                  roleKeycloack={roleKeycloack}
                  func={dashboardFunc.auditoriaPesquisar}
                >
                  <Button
                    type="submit"
                    translate="no"
                    color="#FFF"
                    backgroundColor="#169499"
                    fontWeight={700}
                    fontSize="15px"
                    lineHeight="21.6px"
                    _hover={{ bg: "#169499" }}
                    isLoading={loading}
                  >
                    Pesquisar
                  </Button>
                </Permission>
                <Button
                  color="#FFF"
                  backgroundColor="#169499"
                  fontWeight={700}
                  fontSize="15px"
                  lineHeight="21.6px"
                  _hover={{ bg: "#169499" }}
                  onClick={() => {
                    props.resetForm({ values: initialFilters });
                    setVisaoCatalogo(initialFilters.visao.value);
                    onClear();
                  }}
                >
                  Limpar
                </Button>
              </SimpleGrid>
            </Flex>
          </Form>
        )}
      </Formik>

      <Divider
        borderColor="#DDEBEA"
        orientation="horizontal"
        mt="18px"
        h="2px"
      />
    </Flex>
  );
}
